import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
    Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import s from './AnalyticsTab.module.scss';

// Сетка и оси намеренно бледные: данные должны быть заметнее разметки
const AXIS = { stroke: '#9ca3af', fontSize: 11 };
const GRID = '#eef2f7';
const ACCENT = '#2563eb';

// Прежние 8 показателей временно убраны с экрана в пользу разбивки по
// источникам - код и разметка остаются как есть, включаются обратно
// одним флагом, без реконструкции JSX
const SHOW_LEGACY_CHARTS = false;

// Семантика стадий B24: P - в работе, S - успешно, F - отказ.
// Красный намеренно не используется: отказ кандидату это штатный исход, а не
// ошибка. Плюс пара зелёный/красный неразличима при дейтеранопии (проверено
// валидатором: ΔE 4.2), а зелёный/синий/янтарный дают 27.4 - успех и отказ
// не сливаются у дальтоников, для дашборда найма это критично
const SEMANTICS = {
    S: { label: 'Успешно', color: '#15803d' },
    P: { label: 'В работе', color: '#2563eb' },
    F: { label: 'Отказ', color: '#b45309' },
    unknown: { label: 'Без стадии', color: '#9ca3af' },
};
const SEMANTIC_ORDER = ['S', 'P', 'F', 'unknown'];

const selectStyles = {
    control: (base) => ({ ...base, minHeight: 34, borderColor: '#e5e7eb', fontSize: 13 }),
    menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

function plural(n, one, few, many) {
    const mod10 = n % 10, mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}

const days = (v) => (v === null || v === undefined ? '-' : `${v} ${plural(Math.round(v), 'день', 'дня', 'дней')}`);

/** Плитка с одним числом. Для одиночного показателя диаграмма не нужна -
 *  число само себе визуализация. */
function StatTile({ label, value, hint, big, delta }) {
    return (
        <div className={`${s.tile} ${big ? s.tileBig : ''}`}>
            <div className={s.tileLabel}>{label}</div>
            <div className={s.tileValue}>{value}</div>
            {hint && <div className={s.tileHint}>{hint}</div>}
            {delta}
        </div>
    );
}

/** Всплывающая подсказка. Одна на все графики: разный вид тултипов на соседних
 *  диаграммах заставляет каждый раз заново разбираться, что где написано. */
function ChartTooltip({ active, payload, label, rows }) {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
        <div className={s.tooltip}>
            <div className={s.tooltipTitle}>{point.fullLabel || label}</div>
            {rows(point).filter(Boolean).map((row, i) => (
                <div key={i} className={s.tooltipRow}>
                    <span className={s.tooltipKey}>{row[0]}</span>
                    <span className={s.tooltipValue}>{row[1]}</span>
                </div>
            ))}
        </div>
    );
}

/** Высота графика по числу строк: фиксированная высота либо давит короткие
 *  списки пустотой, либо сплющивает длинные до нечитаемости. */
const chartHeight = (rows) => Math.max(120, rows * 34 + 40);

const shortLabel = (text, limit = 22) =>
    (text || '').length > limit ? `${text.slice(0, limit - 1)}…` : (text || '');

// ============================== период: месяц/квартал/год ==============================
// Произвольные даты «с» и «по» звали сравнивать несравнимое - 17 дней марта
// с половиной апреля. Пикер всегда даёт целый календарный отрезок, и именно
// поэтому дельта к прошлому периоду (DeltaBadge) сравнивает как для как:
// месяц с предыдущим месяцем, а не с рандомным числом дней перед ним

const MONTHS_FULL = [
    'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
    'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

const toISODate = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

function periodRange(mode, anchor, customRange) {
    if (mode === 'custom') return { from: customRange?.from || '', to: customRange?.to || '' };
    if (mode === 'all') return { from: '', to: '' };
    const y = anchor.getFullYear();
    if (mode === 'year') {
        return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    if (mode === 'quarter') {
        const startMonth = Math.floor(anchor.getMonth() / 3) * 3;
        return {
            from: toISODate(new Date(y, startMonth, 1)),
            // День 0 следующего месяца - последний день текущего
            to: toISODate(new Date(y, startMonth + 3, 0)),
        };
    }
    return {
        from: toISODate(new Date(y, anchor.getMonth(), 1)),
        to: toISODate(new Date(y, anchor.getMonth() + 1, 0)),
    };
}

function periodLabel(mode, anchor) {
    if (mode === 'year') return `${anchor.getFullYear()}`;
    if (mode === 'quarter') {
        return `${Math.floor(anchor.getMonth() / 3) + 1} квартал ${anchor.getFullYear()}`;
    }
    return `${MONTHS_FULL[anchor.getMonth()]} ${anchor.getFullYear()}`;
}

function shiftPeriod(mode, anchor, dir) {
    const d = new Date(anchor);
    if (mode === 'year') d.setFullYear(d.getFullYear() + dir);
    else if (mode === 'quarter') d.setMonth(d.getMonth() + dir * 3);
    else d.setMonth(d.getMonth() + dir);
    return d;
}

const PERIOD_PRESETS = [
    { key: 'all', label: 'Всё время' },
    { key: 'month', label: 'Месяц' },
    { key: 'quarter', label: 'Квартал' },
    { key: 'year', label: 'Год' },
];

/** Подпись на самой кнопке: что выбрано сейчас, без открытия меню. */
function periodTriggerLabel(filters) {
    if (filters.periodMode === 'custom' && filters.customRange?.from) {
        return `${filters.customRange.from} – ${filters.customRange.to}`;
    }
    if (filters.periodMode === 'all') return 'Всё время';
    return periodLabel(filters.periodMode, filters.periodAnchor);
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Сетка дней месяца, понедельник первым. null - клетки до 1 числа, чтобы
 *  сетка не съезжала и первое число всегда стояло под своим днём недели. */
function monthGrid(year, month) {
    const first = new Date(year, month, 1);
    const leadingBlanks = (first.getDay() + 6) % 7; // getDay(): 0=вс -> нужно 0=пн
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = new Array(leadingBlanks).fill(null);
    for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
    return cells;
}

/** Единый выбор периода: пресеты списком строк с галочкой на выбранном
 *  (и встроенной навигацией у активного пресета, если это месяц/квартал/год),
 *  произвольный диапазон дат - за тонкой чертой в подвале того же меню.
 *  Список пресетов, а не сегменты-кнопки: календарную сетку невозможно
 *  быстро прочитать глазами ради "прошлый квартал", а тут это одна строка. */
function PeriodMenu({ filters, onSelectPreset, onShift, onApplyCustom, onClose }) {
    const { periodMode: mode, periodAnchor: anchor, customRange } = filters;
    const [range, setRange] = useState({ from: customRange?.from || null, to: customRange?.to || null });
    const [viewDate, setViewDate] = useState(() => (customRange?.from ? new Date(customRange.from) : new Date()));

    const handleDayClick = (date) => {
        const iso = toISODate(date);
        setRange(prev => {
            if (!prev.from || prev.to) return { from: iso, to: null };
            if (iso < prev.from) return { from: iso, to: prev.from };
            return { from: prev.from, to: iso };
        });
    };
    const inRange = (date) => {
        if (!date || !range.from) return false;
        const iso = toISODate(date);
        return range.to ? (iso >= range.from && iso <= range.to) : iso === range.from;
    };
    const isEdge = (date) => date && (toISODate(date) === range.from || toISODate(date) === range.to);
    const cells = monthGrid(viewDate.getFullYear(), viewDate.getMonth());

    return (
        <div className={s.periodMenu} onClick={e => e.stopPropagation()}>
            {PERIOD_PRESETS.map(p => {
                const active = mode === p.key;
                return (
                    <div
                        key={p.key}
                        className={`${s.periodRow} ${active ? s.periodRowActive : ''}`}
                        onClick={() => onSelectPreset(p.key)}
                    >
                        <span className={s.periodCheck}>{active && '✓'}</span>
                        <span className={s.periodRowLabel}>{p.label}</span>
                        {active && p.key !== 'all' && (
                            <span className={s.periodRowNav} onClick={e => e.stopPropagation()}>
                                <button type="button" onClick={() => onShift(-1)} aria-label="Раньше">‹</button>
                                <span>{periodLabel(mode, anchor)}</span>
                                <button type="button" onClick={() => onShift(1)} aria-label="Позже">›</button>
                            </span>
                        )}
                    </div>
                );
            })}

            <div className={s.periodMenuDivider}/>
            <div className={s.periodMenuCustomLabel}>Свой период</div>

            <div className={s.calendarNav}>
                <button type="button" className={s.periodNavBtn}
                        onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                        aria-label="Предыдущий месяц">‹</button>
                <div className={s.calendarNavLabel}>
                    {MONTHS_FULL[viewDate.getMonth()]} {viewDate.getFullYear()}
                </div>
                <button type="button" className={s.periodNavBtn}
                        onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                        aria-label="Следующий месяц">›</button>
            </div>
            <div className={s.calendarWeekdays}>
                {WEEKDAYS.map(w => <div key={w}>{w}</div>)}
            </div>
            <div className={s.calendarGrid}>
                {cells.map((date, i) => date ? (
                    <button
                        type="button"
                        key={i}
                        className={`${s.calendarDay} ${inRange(date) ? s.calendarDayInRange : ''} ${isEdge(date) ? s.calendarDaySelected : ''}`}
                        onClick={() => handleDayClick(date)}
                    >
                        {date.getDate()}
                    </button>
                ) : <div key={i}/>)}
            </div>
            <div className={s.calendarFooter}>
                <div className={s.calendarRangeLabel}>
                    {range.from ? `${range.from}${range.to ? ` – ${range.to}` : ' – ...'}` : 'Выберите даты'}
                </div>
                <div className={s.calendarActions}>
                    <button type="button" className={s.buttonGhost} onClick={onClose}>Отмена</button>
                    <button
                        type="button"
                        className={s.button}
                        disabled={!range.from}
                        onClick={() => onApplyCustom({ from: range.from, to: range.to || range.from })}
                    >
                        Применить
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Раздел ещё дорабатывается. Говорим об этом сразу и по делу: не «скоро
 *  будет лучше», а что именно значит для того, кто сейчас смотрит цифры. */
function DevBanner() {
    return (
        <div className={s.devBanner}>
            <b>Раздел в разработке.</b> Состав показателей и цифры могут измениться.
        </div>
    );
}

// Первая загрузка считает больше десятка метрик и может занимать заметное
// время - голый текст «Загрузка...» на таком сроке читается как зависание.
// Бегущие столбики и сменяющиеся подписи держат внимание и дают понять,
// что что-то реально происходит, а не браузер завис
const LOADING_MESSAGES = [
    'Считаем воронку найма...',
    'Смотрим, кто на каком этапе...',
    'Сверяем источники кандидатов...',
    'Раскладываем по вакансиям...',
    'Почти готово...',
];

function LoadingScreen() {
    const [messageIndex, setMessageIndex] = useState(0);
    useEffect(() => {
        const id = setInterval(
            () => setMessageIndex(i => (i + 1) % LOADING_MESSAGES.length),
            1800,
        );
        return () => clearInterval(id);
    }, []);
    return (
        <div className={s.loadingScreen}>
            <div className={s.loadingBars}>
                <span/><span/><span/><span/><span/>
            </div>
            <div className={s.loadingText}>{LOADING_MESSAGES[messageIndex]}</div>
        </div>
    );
}

const SEVERITY = {
    problem: { color: '#b91c1c', label: 'Мешает нанимать' },
    attention: { color: '#b45309', label: 'Присмотреться' },
    ok: { color: '#15803d', label: 'В порядке' },
};

/** Разбор от ИИ. Стоит первым на экране сознательно: аналитика нужна ради
 *  вывода, а не ради графиков, и страница должна открываться выводом. */
function Insights({ data, loading, error, onAsk }) {
    if (loading) {
        return <div className={s.insights}><div className={s.insightsIdle}>Читаем цифры...</div></div>;
    }
    if (error) {
        return (
            <div className={s.insights}>
                <div className={s.insightsIdle}>{error}</div>
                <button className={s.buttonGhost} onClick={onAsk}>Повторить</button>
            </div>
        );
    }
    if (!data) {
        return (
            <div className={s.insights}>
                <div className={s.insightsIdle}>
                    Разобрать цифры и подсказать, за что взяться в первую очередь
                </div>
                <button className={s.buttonGhost} onClick={onAsk}>Показать разбор</button>
            </div>
        );
    }
    return (
        <div className={s.insights}>
            <div className={s.insightsSummary}>{data.summary}</div>
            <div className={s.insightsList}>
                {(data.insights || []).filter(i => !i.chart || i.chart === 'general').map((item, i) => {
                    const sev = SEVERITY[item.severity] || SEVERITY.attention;
                    return (
                        <div key={i} className={s.insightItem}>
                            <span className={s.insightDot} style={{ background: sev.color }}
                                  title={sev.label}/>
                            <div>
                                <div className={s.insightTitle}>{item.title}</div>
                                <div className={s.insightDetail}>{item.detail}</div>
                                <div className={s.insightAction}>{item.action}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button className={s.buttonGhost} onClick={onAsk}>Пересобрать разбор</button>
        </div>
    );
}

/** Что стоит за цифрой: карточки, попавшие в срез. Открывается по клику
 *  на график - «медиана 40 дней» без возможности посмотреть, кто именно там
 *  висит, остаётся числом, с которым нечего делать. */
function DrillPanel({ title, cards, loading, host, onClose }) {
    return (
        <div className={s.drillOverlay} onClick={onClose}>
            <div className={s.drillPanel} onClick={e => e.stopPropagation()}>
                <div className={s.drillHead}>
                    <div className={s.drillTitle}>{title}</div>
                    <button className={s.drillClose} onClick={onClose} aria-label="Закрыть">×</button>
                </div>
                {loading ? (
                    <div className={s.drillEmpty}>Загружаем карточки...</div>
                ) : !cards.length ? (
                    <div className={s.drillEmpty}>Карточек под этот срез нет.</div>
                ) : (
                    <div className={s.drillList}>
                        {cards.map(c => {
                            // Ссылка в сам Битрикс: с дашборда нужно попадать
                            // в карточку, а не переписывать её название в поиск
                            const link = host
                                ? `https://${host}/crm/type/${c.entity_type_id}/details/${c.b24_element_id}/`
                                : null;
                            return (
                                <div key={`${c.entity_type_id}-${c.b24_element_id}`} className={s.drillRow}>
                                    <div className={s.drillMain}>
                                        {link ? (
                                            <a href={link} target="_blank" rel="noreferrer"
                                               className={s.drillLink}>
                                                {c.title || `#${c.b24_element_id}`}
                                            </a>
                                        ) : (
                                            <span>{c.title || `#${c.b24_element_id}`}</span>
                                        )}
                                        <div className={s.drillMeta}>
                                            {c.stage_name || c.stage_id || 'Без стадии'}
                                            {c.assigned_by_name ? ` · ${c.assigned_by_name}` : ''}
                                        </div>
                                    </div>
                                    <div className={s.drillDays}>
                                        {c.days_on_stage != null ? days(c.days_on_stage) : '-'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Наблюдения, относящиеся к одному графику. Стоят под ним, а не общим
 *  списком наверху: разбор рядом с цифрами, о которых он говорит, читается
 *  без перевода взгляда туда-обратно. */
function ChartInsights({ items }) {
    if (!items || !items.length) return null;
    return (
        <div className={s.chartInsights}>
            {items.map((item, i) => {
                const sev = SEVERITY[item.severity] || SEVERITY.attention;
                return (
                    <div key={i} className={s.insightItem}>
                        <span className={s.insightDot} style={{ background: sev.color }}
                              title={sev.label}/>
                        <div>
                            <div className={s.insightTitle}>{item.title}</div>
                            <div className={s.insightDetail}>{item.detail}</div>
                            <div className={s.insightAction}>{item.action}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** Цвет столбца с учётом того, указал ли на него разбор. Подсвеченный
 *  остаётся того же тона, но насыщеннее: другой цвет читался бы как другая
 *  категория данных, а это тот же показатель, просто на него смотрят. */
const barFill = (isTarget, base, highlight) => (isTarget ? highlight : base);

/** График длительности этапов. Вынесен в компонент, потому что рисуется
 *  дважды: этапы кандидатов и этапы вакансий. Смешивать их в одном списке
 *  нельзя - это разные процессы, и «Новая вакансия» рядом с «Собеседованием»
 *  читается как сбой данных. */
/** Разложить строки по воронкам. Этапы принадлежат воронке, и в разных
 *  воронках наборы разные - в одном списке они читаются как каша. */
function byCategory(rows) {
    const groups = new Map();
    rows.forEach(r => {
        const key = r.category_id ?? 'none';
        if (!groups.has(key)) {
            groups.set(key, { name: r.category_name, id: r.category_id, rows: [] });
        }
        groups.get(key).rows.push(r);
    });
    return [...groups.values()];
}

/** То же самое, но по HR - для карточки «Загрузка эйчаров»: та же воронка,
 *  что и общая, но раскрытая на всех сотрудников разом, каждый своим блоком. */
function byManager(rows) {
    const groups = new Map();
    rows.forEach(r => {
        const key = r.assigned_by_id ?? 'none';
        if (!groups.has(key)) {
            groups.set(key, { name: r.assigned_by_name, id: r.assigned_by_id, rows: [] });
        }
        groups.get(key).rows.push(r);
    });
    return [...groups.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// Категориальная палитра, порядок фиксирован (не перебирается по кругу) -
// проверена скриптом на различимость при дальтонизме и контраст: blue,
// orange, aqua, yellow, magenta, green, violet, red. Используется как
// запасной вариант для источников, которых нет в SOURCE_COLOR_MAP
const SOURCE_COLORS = [
    '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];

// Фирменные цвета источников важнее рядового номера в палитре: HH - его
// собственный красный (узнаваемость канала важнее внутренней логики
// категориальной последовательности), «вручную через расширение» - синий
// акцент проекта. Источники, которых здесь нет, идут по SOURCE_COLORS
const SOURCE_COLOR_MAP = {
    'Автоотклик': '#d6001c',
    'hh.ru': '#d6001c',
    'Браузерное расширение': '#2563eb',
};

// Отказы - в приглушённой серой гамме отдельно от источников: это негативный
// исход, ему не место среди ярких цветов канала, и так делает референс
// (Хантфлоу), на который ориентировались
const REJECTION_COLORS = ['#78716c', '#a8a29e', '#57534e', '#d6d3d1', '#44403c', '#e7e5e4'];

/** Цвет на метку: сперва фиксированная привязка (SOURCE_COLOR_MAP), для
 *  всего остального - следующий цвет из запасной палитры по счётчику
 *  «сколько меток без фиксированного цвета уже встретилось». */
function buildColorMap(labels, fixedMap, fallbackPalette) {
    const map = {};
    let next = 0;
    labels.forEach(label => {
        if (fixedMap[label]) {
            map[label] = fixedMap[label];
        } else {
            map[label] = fallbackPalette[next % fallbackPalette.length];
            next += 1;
        }
    });
    return map;
}

/** Стабильный цвет на метку - один и тот же источник должен быть одного
 *  цвета в любом этапе и на любом графике, иначе цвет читается как
 *  случайный, а не как принадлежность к каналу. */
function stageSourceColors(rows, fixedMap = SOURCE_COLOR_MAP, fallbackPalette = SOURCE_COLORS) {
    const labels = [];
    rows.forEach(r => { if (!labels.includes(r.label)) labels.push(r.label); });
    return buildColorMap(labels, fixedMap, fallbackPalette);
}

/** Одна полоса на 100% ширины, разделённая на сегменты по источнику - каждый
 *  сегмент шириной ровно в свою долю. Сейчас источник фактически один (HH),
 *  поэтому полоса делится на «Автоотклик»/«Браузерное расширение»: это то
 *  различие, которое видно уже сегодня, а не то, что появится когда-нибудь
 *  с новыми каналами. */
function SourceBar({ rows }) {
    const total = rows.reduce((sum, r) => sum + r.candidates, 0);
    const colorOf = stageSourceColors(rows);
    return (
        <div>
            <div className={s.sourceBar}>
                {rows.map((r, i) => (
                    <div
                        key={i}
                        className={s.sourceSegment}
                        style={{
                            width: `${total ? (r.candidates / total) * 100 : 0}%`,
                            background: colorOf[r.label],
                        }}
                        title={`${r.label}: ${r.candidates} (${total ? Math.round((r.candidates / total) * 100) : 0}%)`}
                    />
                ))}
            </div>
            <div className={s.legend}>
                {rows.map((r, i) => (
                    <span key={i} className={s.legendItem}>
                        <span className={s.legendSwatch} style={{ background: colorOf[r.label] }}/>
                        {r.label} - {r.candidates}
                        {r.share != null ? ` (${Math.round(r.share * 100)}%)` : ''}
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Строит объединённый список этапов из двух независимых наборов строк
 *  (текущее состояние и отказы) по sort_order с бэкенда - у этапа может
 *  не быть живых кандидатов, но быть отказы, и наоборот, потерять такой
 *  этап из списка нельзя. */
function mergeStageRows(pipelineRows, rejectionRows) {
    const stages = new Map();
    const ensure = (stageId, stageName, sortOrder, entityTypeId, categoryId) => {
        if (!stages.has(stageId)) {
            stages.set(stageId, {
                stageId, stageName, sortOrder, entityTypeId, categoryId,
                pipelineSegments: [], pipelineTotal: 0,
                rejectionSegments: [], rejectionTotal: 0,
            });
        }
        const entry = stages.get(stageId);
        if (entry.sortOrder == null && sortOrder != null) entry.sortOrder = sortOrder;
        if (!entry.stageName && stageName) entry.stageName = stageName;
        if (entry.entityTypeId == null && entityTypeId != null) entry.entityTypeId = entityTypeId;
        return entry;
    };
    pipelineRows.forEach(r => {
        const entry = ensure(r.stage_id, r.stage_name, r.sort_order, r.entity_type_id, r.category_id);
        entry.pipelineSegments.push(r);
        entry.pipelineTotal += r.candidates;
    });
    rejectionRows.forEach(r => {
        const entry = ensure(r.stage_id, r.stage_name, r.sort_order);
        entry.rejectionSegments.push(r);
        entry.rejectionTotal += r.candidates;
    });
    return [...stages.values()].sort((a, b) => {
        if (a.sortOrder == null && b.sortOrder == null) return 0;
        if (a.sortOrder == null) return 1;
        if (b.sortOrder == null) return -1;
        return a.sortOrder - b.sortOrder;
    });
}

/** Чем фильтровать провал в карточки по клику на сегмент: конкретный
 *  source_kind/source, либо, если у сегмента ни того ни другого нет
 *  (метка «Не указано»), явный флаг «источник не определён» - без него
 *  клик по такому сегменту показывал бы вообще всех на этапе. */
function sourceOverrides(seg) {
    if (seg.source_kind) return { sourceKind: seg.source_kind };
    if (seg.source) return { source: seg.source };
    return { noSource: true };
}

/** Тот же приём для группы HR в «Загрузке эйчаров»: конкретный сотрудник,
 *  либо явный флаг «ответственный не проставлен» - без него клик по группе
 *  «Без ответственного» показывал бы вообще всех на этапе. */
function managerOverrides(managerId) {
    if (managerId != null) return { assignedById: managerId };
    return { noAssignedBy: true };
}

/** Кто сейчас на каком этапе, по источникам - один столбец баров, легенда
 *  справа (не под графиком): при вертикальном списке этапов легенда сбоку
 *  не разрывает взгляд построчным чтением. */
function StageSourceChart({ rows, onSegmentClick }) {
    const colorOf = useMemo(() => stageSourceColors(rows), [rows]);
    const stages = useMemo(() => mergeStageRows(rows, []), [rows]);
    const maxTotal = Math.max(1, ...stages.map(st => st.pipelineTotal));

    return (
        <div className={s.stageSourceLayout}>
            <div className={s.stageSourceRowsCol}>
                {stages.map(stage => (
                    <div key={stage.stageId} className={`${s.stageSourceRow} ${s.stageSourceRowSingle}`}>
                        <div className={s.stageSourceLabel} title={stage.stageName || stage.stageId}>
                            {shortLabel(stage.stageName || stage.stageId, 20)}
                        </div>
                        <div className={s.stageSourceTrack}>
                            {stage.pipelineTotal > 0 && (
                                <div className={s.stageSourceBar}
                                     style={{ width: `${Math.max(2, (stage.pipelineTotal / maxTotal) * 100)}%` }}>
                                    {stage.pipelineSegments.map((seg, i) => (
                                        <div
                                            key={i}
                                            className={s.stageSourceSegment}
                                            style={{ flex: seg.candidates, background: colorOf[seg.label] }}
                                            onClick={() => onSegmentClick(stage, seg)}
                                        >
                                            <span className={s.stageSourceTooltip}>
                                                {seg.label}: {seg.candidates} {plural(seg.candidates, 'человек', 'человека', 'человек')}
                                                {' '}({stage.pipelineTotal ? Math.round((seg.candidates / stage.pipelineTotal) * 100) : 0}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={s.stageSourceTotal}>{stage.pipelineTotal || ''}</div>
                    </div>
                ))}
            </div>
            {Object.keys(colorOf).length > 0 && (
                <div className={s.stageSourceLegendSide}>
                    <span className={s.stageSourceLegendCaption}>Источники</span>
                    <div className={s.legendVertical}>
                        {Object.entries(colorOf).map(([label, color]) => (
                            <span key={label} className={s.legendItem}>
                                <span className={s.legendSwatch} style={{ background: color }}/>
                                {label}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Воронка найма: дошедшие минимум до этапа (накопительно, левая колонка)
 *  и отказы после этапа (правая) - на одних и тех же строках, тем же стилем,
 *  что и «кто сейчас на каком этапе». Рядом с числом слева - процент, сколько
 *  из дошедших сюда прошли ДАЛЬШЕ, к следующему этапу (не «сколько всего»,
 *  а именно «сколько продолжили»). Последняя строка - выход на работу, если
 *  такой этап есть в воронке дальше него сравнивать не с чем, процента нет. */
function HiringFunnelChart({ pipelineRows, rejectionRows, onPipelineClick, onRejectionClick }) {
    const pipelineColor = useMemo(() => stageSourceColors(pipelineRows), [pipelineRows]);
    const rejectionColor = useMemo(
        () => stageSourceColors(rejectionRows, {}, REJECTION_COLORS),
        [rejectionRows],
    );
    const stages = useMemo(
        () => mergeStageRows(pipelineRows, rejectionRows),
        [pipelineRows, rejectionRows],
    );
    const maxPipeline = Math.max(1, ...stages.map(st => st.pipelineTotal));
    const maxRejection = Math.max(1, ...stages.map(st => st.rejectionTotal));

    return (
        <div>
            <div className={`${s.stageSourceRow} ${s.stageSourceRowDouble} ${s.stageSourceHead}`}>
                <div/>
                <div className={s.stageSourceHeadLabel}>Дошли до этапа</div>
                <div/>
                <div className={s.stageSourceHeadLabel}>Отказы после этапа</div>
                <div/>
            </div>
            {stages.map((stage, i) => {
                const next = stages[i + 1];
                const forwardPct = next && stage.pipelineTotal
                    ? Math.round((next.pipelineTotal / stage.pipelineTotal) * 100)
                    : null;
                return (
                    <div key={stage.stageId} className={`${s.stageSourceRow} ${s.stageSourceRowDouble}`}>
                        <div className={s.stageSourceLabel} title={stage.stageName || stage.stageId}>
                            {shortLabel(stage.stageName || stage.stageId, 20)}
                        </div>
                        <div className={s.stageSourceTrack}>
                            {stage.pipelineTotal > 0 && (
                                <div className={s.stageSourceBar}
                                     style={{ width: `${Math.max(2, (stage.pipelineTotal / maxPipeline) * 100)}%` }}>
                                    {stage.pipelineSegments.map((seg, j) => (
                                        <div
                                            key={j}
                                            className={s.stageSourceSegment}
                                            style={{ flex: seg.candidates, background: pipelineColor[seg.label] }}
                                            onClick={() => onPipelineClick(stage, seg)}
                                        >
                                            <span className={s.stageSourceTooltip}>
                                                {seg.label}: {seg.candidates} {plural(seg.candidates, 'человек', 'человека', 'человек')}
                                                {' '}({stage.pipelineTotal ? Math.round((seg.candidates / stage.pipelineTotal) * 100) : 0}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={s.stageSourceTotal}>
                            {stage.pipelineTotal || ''}
                            {forwardPct != null && (
                                <span className={s.stageSourceForward}> · ↓ {forwardPct}%</span>
                            )}
                        </div>
                        <div className={s.stageSourceTrack}>
                            {stage.rejectionTotal > 0 && (
                                <div className={s.stageSourceBar}
                                     style={{ width: `${Math.max(2, (stage.rejectionTotal / maxRejection) * 100)}%` }}>
                                    {stage.rejectionSegments.map((seg, j) => (
                                        <div
                                            key={j}
                                            className={s.stageSourceSegment}
                                            style={{ flex: seg.candidates, background: rejectionColor[seg.label] }}
                                            onClick={() => onRejectionClick(stage, seg)}
                                        >
                                            <span className={s.stageSourceTooltip}>
                                                {seg.label}: {seg.candidates} {plural(seg.candidates, 'человек', 'человека', 'человек')}
                                                {' '}({stage.rejectionTotal ? Math.round((seg.candidates / stage.rejectionTotal) * 100) : 0}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={s.stageSourceTotal}>{stage.rejectionTotal || ''}</div>
                    </div>
                );
            })}
            <div className={s.stageSourceLegends}>
                {Object.keys(pipelineColor).length > 0 && (
                    <div className={s.stageSourceLegendGroup}>
                        <span className={s.stageSourceLegendCaption}>Источники</span>
                        <Legend items={Object.entries(pipelineColor).map(([label, color]) => (
                            { key: label, label, color }
                        ))}/>
                    </div>
                )}
                {Object.keys(rejectionColor).length > 0 && (
                    <div className={s.stageSourceLegendGroup}>
                        <span className={s.stageSourceLegendCaption}>Причины отказа</span>
                        <Legend items={Object.entries(rejectionColor).map(([label, color]) => (
                            { key: label, label, color }
                        ))}/>
                    </div>
                )}
            </div>
        </div>
    );
}

function StageDurationChart({ rows, targets, onDrill, includeVacancies }) {
    return (
        <ResponsiveContainer width="100%" height={chartHeight(rows.length)}>
            <BarChart
                data={rows.map(r => ({
                    name: shortLabel(r.stage_name || r.stage_id),
                    fullLabel: r.stage_name || r.stage_id || 'Без стадии',
                    stageId: r.stage_id,
                    median: r.median_days,
                    p90: r.p90_days,
                    transitions: r.transitions,
                    now: r.currently_on_stage,
                }))}
                layout="vertical"
                margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
            >
                <CartesianGrid stroke={GRID} horizontal={false}/>
                <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="name" width={150}
                       {...AXIS} tickLine={false} axisLine={false}/>
                <Tooltip
                    cursor={{ fill: '#f8fafc' }}
                    content={<ChartTooltip rows={p => [
                        ['Обычно столько', days(p.median)],
                        p.p90 != null && ['Почти всегда быстрее', days(p.p90)],
                        ['Прошли этап', p.transitions],
                        ['Стоят сейчас', p.now],
                    ]}/>}
                />
                <Bar dataKey="median" radius={[0, 4, 4, 0]} barSize={16} cursor="pointer"
                     onClick={(p) => onDrill(`Этап: ${p.fullLabel}`,
                         { stageId: p.stageId, includeVacancies })}>
                    {rows.map(r => (
                        <Cell key={r.stage_id}
                              fill={barFill(targets[`stage_durations:${r.stage_id}`], ACCENT, '#1e3a8a')}/>
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

/** Узлы графа - этапы по порядку воронки (сначала пришедшие в rows от
 *  бэкенда, потом отсортированные по sort_order). Этап попадает в список,
 *  даже если сам никуда не ведёт (конечная стадия) - иначе путь обрывается
 *  видимым куском, а не концом. */
function buildFlowStages(rows) {
    const byId = new Map();
    const put = (id, name, order) => {
        if (!id) return;
        const existing = byId.get(id);
        if (existing) {
            if (existing.order == null && order != null) existing.order = order;
            return;
        }
        byId.set(id, { id, name: name || id, order: order ?? null });
    };
    rows.forEach(r => {
        put(r.from_stage, r.from_stage_name, r.from_sort_order);
        put(r.to_stage, r.to_stage_name, r.to_sort_order);
    });
    return [...byId.values()].sort((a, b) => {
        if (a.order == null && b.order == null) return 0;
        if (a.order == null) return 1;
        if (b.order == null) return -1;
        return a.order - b.order;
    });
}

/** Граф переходов как вертикальный степпер: этап, а сразу под ним - все
 *  переходы, которые из него реально были (в скольких людях и за сколько
 *  дней), дальше следующий этап. Не диаграмма со стрелками поверх узлов:
 *  возвраты назад образуют цикл (A→B и B→A), под который нет готового
 *  компонента раскладки, а рисовать пересекающиеся кривые руками - большой
 *  риск нечитаемой каши без возможности проверить это глазами. Степпер
 *  той же информации не теряет и не может наложиться сам на себя. */
function TransitionsChart({ rows, onDrill }) {
    const stages = buildFlowStages(rows);
    const edgesFrom = useMemo(() => {
        const map = new Map();
        rows.forEach(r => {
            if (!map.has(r.from_stage)) map.set(r.from_stage, []);
            map.get(r.from_stage).push(r);
        });
        map.forEach(list => list.sort((a, b) => b.candidates - a.candidates));
        return map;
    }, [rows]);

    return (
        <div className={s.flow}>
            {stages.map(stage => {
                const edges = edgesFrom.get(stage.id) || [];
                return (
                    <div key={stage.id} className={s.flowNode}>
                        <span className={s.flowDot}/>
                        <div className={s.flowNodeName}>{stage.name}</div>
                        {edges.length === 0 ? (
                            <div className={s.flowEmpty}>Дальше кандидаты не двигались</div>
                        ) : (
                            <div className={s.flowEdges}>
                                {edges.map((e, i) => (
                                    <div
                                        key={i}
                                        className={`${s.flowEdge} ${e.is_backward ? s.flowEdgeBack : ''}`}
                                        onClick={() => onDrill(
                                            `Этап: ${e.to_stage_name || e.to_stage}`,
                                            { stageId: e.to_stage },
                                        )}
                                    >
                                        <span className={s.flowEdgeArrow}>{e.is_backward ? '↩' : '→'}</span>
                                        <span className={s.flowEdgeName}>{e.to_stage_name || e.to_stage}</span>
                                        <span className={s.flowEdgeMeta}>
                                            {e.candidates} {plural(e.candidates, 'чел', 'чел', 'чел')}
                                            {e.median_days != null && ` · ${days(e.median_days)}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/** Настройки раздела. Заводились не для красоты: какие направления относятся
 *  к подбору, а какие к адаптации, из данных вывести нельзя - карточка нанятого
 *  переезжает в адаптацию вместе со связью с вакансией. */
function SettingsPanel({ data, saving, onToggleDirection, onToggleChart, onSetHiringType, onSave, onClose }) {
    if (!data) return null;
    const chosen = new Set(data.hiring_categories || []);
    const hidden = new Set(data.hidden_charts || []);
    const hiringType = data.hiring_type || 'regular';
    return (
        <div className={s.settings}>
            <div className={s.settingsHead}>
                <div className={s.settingsTitle}>Настройки аналитики</div>
                <button className={s.drillClose} onClick={onClose} aria-label="Закрыть">×</button>
            </div>

            <div className={s.settingsBlock}>
                <div className={s.settingsLabel}>Тип подбора</div>
                <div className={s.settingsHint}>
                    От этого зависит, что считать закрытием вакансии в "Срок закрытия
                    вакансии" и в сводных числах наверху
                </div>
                <label className={s.settingsRow}>
                    <input type="radio" name="hiringType" checked={hiringType === 'regular'}
                           onChange={() => onSetHiringType('regular')}/>
                    <span>Регулярный - вакансия не закрывается сама, закрытие это найм человека</span>
                </label>
                <label className={s.settingsRow}>
                    <input type="radio" name="hiringType" checked={hiringType === 'one_off'}
                           onChange={() => onSetHiringType('one_off')}/>
                    <span>Единичный - закрытие это закрытие самой карточки вакансии</span>
                </label>
            </div>

            <div className={s.settingsBlock}>
                <div className={s.settingsLabel}>Направления подбора</div>
                <div className={s.settingsHint}>
                    Отметьте те, где ведётся найм. Остальные - адаптация, онбординг
                    и прочее - в аналитику не попадут. Если не отмечено ничего,
                    мы пробуем определить их сами, но точность не гарантируем
                </div>
                {(data.directions || []).map(d => (
                    <label key={d.key} className={s.settingsRow}>
                        <input type="checkbox" checked={chosen.has(d.key)}
                               onChange={() => onToggleDirection(d.key)}/>
                        <span>{d.name}</span>
                        <span className={s.settingsCount}>{d.items} карточек</span>
                    </label>
                ))}
            </div>

            <div className={s.settingsBlock}>
                <div className={s.settingsLabel}>Показатели на экране</div>
                <div className={s.settingsHint}>Снимите галочку, чтобы скрыть показатель</div>
                {(data.charts || []).map(c => (
                    <label key={c.id} className={s.settingsRow}>
                        <input type="checkbox" checked={!hidden.has(c.id)}
                               onChange={() => onToggleChart(c.id)}/>
                        <span>{c.title}</span>
                    </label>
                ))}
            </div>

            <button className={s.button} onClick={onSave} disabled={saving}>
                {saving ? 'Сохраняем...' : 'Сохранить'}
            </button>
        </div>
    );
}

/** Заголовок карточки, по которому она сворачивается. Специально не отдельная
 *  кнопка сбоку, а весь заголовок целиком - цель побольше проще попасть
 *  курсором, да и на мобильном экране это уже привычный жест. */
function CardTitle({ id, title, collapsed, onToggle }) {
    return (
        <div className={s.cardTitle} onClick={() => onToggle(id)}>
            <span>{title}</span>
            <span className={s.cardToggle} aria-label={collapsed ? 'Развернуть' : 'Свернуть'}>
                {collapsed ? '▸' : '▾'}
            </span>
        </div>
    );
}

function Legend({ items }) {
    return (
        <div className={s.legend}>
            {items.map(i => (
                <span key={i.key} className={s.legendItem}>
                    <span className={s.legendSwatch} style={{ background: i.color }}/>
                    {i.label}
                </span>
            ))}
        </div>
    );
}

export default function AnalyticsTab({ api }) {
    const [filters, setFilters] = useState({
        periodMode: 'all', periodAnchor: new Date(), customRange: null,
        assignedById: null, vacancyId: null,
    });
    const [options, setOptions] = useState({ managers: [], vacancies: [], dataSince: null });
    const [status, setStatus] = useState(null);
    const [data, setData] = useState({
        managerLoad: { rows: [], source: 'current', as_of: null },
        stageDurations: [], timeToHire: null, timeToFill: null, difficulty: [], funnel: [],
        timeline: { bucket: 'month', points: [] }, transitions: [], sourceBreakdown: [],
        stageSources: [], stageRejections: [], hiringFunnel: [], hiringStats: null,
    });
    const [loading, setLoading] = useState(true);
    // Первая загрузка занимает весь экран - показывать больше нечего. Смена
    // фильтра - это уже повторная загрузка поверх готового экрана, и полный
    // экран "Загрузка..." на ней выглядит как то, что всё сломалось и
    // отрисовывается заново, хотя на деле меняются только цифры
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    // Подсказки не грузим вместе с метриками: вызов платный, пусть его
    // запрашивают осознанно
    const [insights, setInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [insightsError, setInsightsError] = useState('');
    const [drill, setDrill] = useState(null);          // {title, stageId}
    const [drillCards, setDrillCards] = useState([]);
    const [drillLoading, setDrillLoading] = useState(false);
    const [portalHost, setPortalHost] = useState(null);
    const [settings, setSettings] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    // Свёрнутые графики - локально на экране, не в настройках портала:
    // "скрыть навсегда" и "сейчас не мешайся" разные действия, путать их
    // в одном хранилище не стоит
    const [collapsedCharts, setCollapsedCharts] = useState({});

    const period = useMemo(
        () => periodRange(filters.periodMode, filters.periodAnchor, filters.customRange),
        [filters.periodMode, filters.periodAnchor, filters.customRange],
    );

    const query = useMemo(() => {
        const p = new URLSearchParams();
        if (period.from) p.set('date_from', period.from);
        if (period.to) p.set('date_to', period.to);
        if (filters.assignedById) p.set('assigned_by_id', filters.assignedById);
        if (filters.vacancyId) p.set('vacancy_id', filters.vacancyId);
        const qs = p.toString();
        return qs ? `?${qs}` : '';
    }, [filters.assignedById, filters.vacancyId, period]);

    // Загрузка эйчаров - та же воронка, что и общая, но раскрытая на всех
    // сотрудников разом (by_manager=true - каждый со своей независимой
    // накопительной воронкой), без отдельного пикера: фильтр «Сотрудник»
    // наверху экрана уже есть и работает как обычно - сузит до одного,
    // если выбран
    const [hrFunnelPipeline, setHrFunnelPipeline] = useState([]);
    const [hrFunnelRejections, setHrFunnelRejections] = useState([]);
    const [hrFunnelLoading, setHrFunnelLoading] = useState(false);

    useEffect(() => {
        setHrFunnelLoading(true);
        const p = new URLSearchParams(query.replace(/^\?/, ''));
        p.set('by_manager', 'true');
        const qs = p.toString();
        Promise.all([
            api.get(`/analytics/hiring-funnel?${qs}`),
            api.get(`/analytics/stage-rejections?${qs}`),
        ])
            .then(([pipeline, rejections]) => {
                setHrFunnelPipeline(pipeline.data || []);
                setHrFunnelRejections(rejections.data || []);
            })
            .catch(() => { setHrFunnelPipeline([]); setHrFunnelRejections([]); })
            .finally(() => setHrFunnelLoading(false));
    }, [query, api]);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        // Один поход за всеми метриками сразу вместо одиннадцати отдельных -
        // раньше каждая смена фильтра слала по запросу на каждый график
        // (плюс свой CORS-preflight на каждый), и это было заметно на глаз
        return Promise.all([
            api.get(`/analytics/status`),
            // Списки взаимно сужаются уже выбранными фильтрами (см. бэкенд,
            // available_filters) - выбрали HR, вакансии предлагаются только
            // те, с которыми он реально работал, и наоборот
            api.get(`/analytics/filters${query}`),
            api.get(`/analytics/dashboard${query}`),
        ])
            .then(([st, f, dash]) => {
                setStatus(st.data);
                setOptions({
                    managers: f.data?.managers || [],
                    vacancies: f.data?.vacancies || [],
                    dataSince: f.data?.data_since || null,
                });
                const d = dash.data || {};
                setData({
                    managerLoad: d.manager_load || { rows: [], source: 'current', as_of: null },
                    stageDurations: d.stage_durations || [],
                    timeToHire: d.time_to_hire || null,
                    timeToFill: d.time_to_fill || null,
                    difficulty: d.difficulty || [],
                    funnel: d.funnel || [],
                    timeline: d.timeline || { bucket: 'month', points: [] },
                    transitions: d.transitions || [],
                    sourceBreakdown: d.source_breakdown || [],
                    stageSources: d.stage_sources || [],
                    stageRejections: d.stage_rejections || [],
                    hiringFunnel: d.hiring_funnel || [],
                    hiringStats: d.hiring_stats || null,
                });
            })
            .catch(e => setError(
                e?.response?.data?.detail || 'Не удалось загрузить аналитику. Попробуйте ещё раз.'
            ))
            .finally(() => { setLoading(false); setHasLoadedOnce(true); });
    }, [api, query]);

    useEffect(() => { load(); }, [load]);

    // Списки сотрудников/вакансий сужаются друг другом (см. /analytics/filters
    // выше) - если уже выбранное значение перестало входить в новый список
    // (например, сузили период так, что HR и вакансия больше не пересекаются),
    // сбрасываем его, а не оставляем невидимый в выпадашке, но всё ещё
    // применённый к дашборду фильтр
    useEffect(() => {
        if (filters.assignedById != null && options.managers.length > 0
            && !options.managers.some(m => m.id === filters.assignedById)) {
            setFilters(f => ({ ...f, assignedById: null }));
        }
    }, [options.managers, filters.assignedById]);

    useEffect(() => {
        if (filters.vacancyId != null && options.vacancies.length > 0
            && !options.vacancies.some(v => v.id === filters.vacancyId)) {
            setFilters(f => ({ ...f, vacancyId: null }));
        }
    }, [options.vacancies, filters.vacancyId]);

    // Хост нужен только чтобы собрать ссылку в карточку Битрикса
    useEffect(() => {
        api.get('/bitrix_portals/portals/current')
            .then(r => setPortalHost(r.data?.host || null))
            .catch(() => {});
    }, [api]);

    useEffect(() => {
        api.get('/analytics/settings')
            .then(r => setSettings(r.data))
            .catch(() => {});
    }, [api]);

    const toggleDirection = (key) => setSettings(prev => {
        const chosen = new Set(prev.hiring_categories || []);
        chosen.has(key) ? chosen.delete(key) : chosen.add(key);
        return { ...prev, hiring_categories: [...chosen] };
    });

    const toggleChart = (id) => setSettings(prev => {
        const hidden = new Set(prev.hidden_charts || []);
        hidden.has(id) ? hidden.delete(id) : hidden.add(id);
        return { ...prev, hidden_charts: [...hidden] };
    });

    const setHiringType = (hiring_type) => setSettings(prev => ({ ...prev, hiring_type }));

    const saveSettings = () => {
        setSettingsSaving(true);
        api.put('/analytics/settings', {
            hiring_categories: settings.hiring_categories || [],
            hidden_charts: settings.hidden_charts || [],
            hiring_type: settings.hiring_type || 'regular',
        })
            // Направления меняют выборку данных, поэтому дашборды пересчитываем
            .then(() => { setSettingsOpen(false); return load(); })
            .catch(e => setError(e?.response?.data?.detail || 'Не удалось сохранить настройки.'))
            .finally(() => setSettingsSaving(false));
    };

    const isHidden = useCallback(
        (chartId) => (settings?.hidden_charts || []).includes(chartId),
        [settings],
    );

    const isCollapsed = useCallback(
        (chartId) => !!collapsedCharts[chartId],
        [collapsedCharts],
    );
    const toggleCollapse = useCallback(
        (chartId) => setCollapsedCharts(prev => ({ ...prev, [chartId]: !prev[chartId] })),
        [],
    );

    // overrides подменяет соответствующий параметр среза (или добавляет его,
    // если в общих фильтрах его не было) - не трогая сами фильтры экрана:
    // провалившись по клику, не хочется, чтобы у всех остальных графиков
    // тоже вдруг подставился фильтр
    const openDrill = useCallback((title, overrides = {}) => {
        setDrill({ title });
        setDrillLoading(true);
        const p = new URLSearchParams(query.replace(/^\?/, ''));
        if (overrides.stageId) p.set('stage_id', overrides.stageId);
        if (overrides.assignedById != null) p.set('assigned_by_id', overrides.assignedById);
        if (overrides.noAssignedBy) {
            // Глобальный фильтр «Сотрудник» мог уже положить свой
            // assigned_by_id в базовый query - вместе с no_assigned_by это
            // было бы противоречием (конкретный сотрудник И «ответственного
            // нет» разом), которое всегда отдаёт пустой список
            p.delete('assigned_by_id');
            p.set('no_assigned_by', 'true');
        }
        if (overrides.vacancyId != null) p.set('vacancy_id', overrides.vacancyId);
        if (overrides.sourceKind != null) p.set('source_kind', overrides.sourceKind);
        if (overrides.source != null) p.set('source', overrides.source);
        if (overrides.noSource) p.set('no_source', 'true');
        if (overrides.rejectedAfterStage) p.set('rejected_after_stage', overrides.rejectedAfterStage);
        if (overrides.rejectionReason != null) p.set('rejection_reason', overrides.rejectionReason);
        if (overrides.funnelStageId) {
            p.set('funnel_stage_id', overrides.funnelStageId);
            p.set('funnel_entity_type_id', overrides.funnelEntityTypeId);
            p.set('funnel_category_id', overrides.funnelCategoryId ?? 0);
            p.set('funnel_sort_order', overrides.funnelSortOrder);
        }
        if (overrides.includeVacancies) p.set('include_vacancies', 'true');
        const qs = p.toString();
        api.get(`/analytics/cards${qs ? `?${qs}` : ''}`)
            .then(r => setDrillCards(r.data || []))
            .catch(() => setDrillCards([]))
            .finally(() => setDrillLoading(false));
    }, [api, query]);

    // Смена фильтров делает прежний разбор неверным - убираем его,
    // чтобы под новыми цифрами не остался вывод по старым
    useEffect(() => { setInsights(null); setInsightsError(''); }, [query]);

    const askInsights = () => {
        setInsightsLoading(true);
        setInsightsError('');
        api.post(`/analytics/insights${query}`)
            .then(r => setInsights(r.data))
            .catch(e => setInsightsError(
                e?.response?.data?.detail || 'Не удалось получить разбор. Попробуйте позже.'
            ))
            .finally(() => setInsightsLoading(false));
    };

    const refresh = () => {
        setRefreshing(true);
        setNotice('');
        api.post('/analytics/refresh')
            .then(r => {
                const d = r.data || {};
                setNotice(
                    d.has_more
                        ? `Загружено событий: ${d.events}. Данных больше, чем помещается за раз - нажмите ещё раз, чтобы продолжить.`
                        : `Обновлено. Новых событий: ${d.events}, карточек: ${d.items}.`
                );
                return load();
            })
            .catch(e => setError(
                e?.response?.data?.detail || 'Не удалось обновить данные.'
            ))
            .finally(() => setRefreshing(false));
    };

    const exportXlsx = () => {
        setExporting(true);
        // Тот же query, что и у дашборда - выгрузка идёт с теми же фильтрами,
        // что сейчас на экране, а не всегда "за всё время"
        api.get(`/analytics/export.xlsx${query}`, { responseType: 'blob' })
            .then(r => {
                const url = URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `Аналитика ${new Date().toLocaleDateString('ru-RU')}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            })
            .catch(() => setError('Не удалось выгрузить файл. Попробуйте позже.'))
            .finally(() => setExporting(false));
    };

    const managerRows = useMemo(() => {
        const byManager = new Map();
        (data.managerLoad?.rows || []).forEach(row => {
            const key = row.assigned_by_id ?? 'none';
            if (!byManager.has(key)) {
                byManager.set(key, {
                    id: row.assigned_by_id,
                    // Имя приходит из справочника B24; его может не быть -
                    // например, сотрудника удалили. Тогда честнее показать ID,
                    // чем пустую строку
                    name: row.assigned_by_name,
                    total: 0,
                    bySemantic: {},
                });
            }
            const entry = byManager.get(key);
            entry.total += row.items_count;
            const sem = row.stage_semantic_id || 'unknown';
            entry.bySemantic[sem] = (entry.bySemantic[sem] || 0) + row.items_count;
        });
        return [...byManager.values()].sort((a, b) => b.total - a.total);
    }, [data.managerLoad]);

    const durationRows = data.stageDurations.filter(r => r.median_days !== null);
    // Этапы кандидатов и этапы вакансий - разные процессы. В одном списке
    // «Новая вакансия» рядом с «Собеседованием» читается как сбой данных
    const candidateStages = durationRows.filter(r => r.kind !== 'vacancy');
    const vacancyStages = durationRows.filter(r => r.kind === 'vacancy');

    // Предупреждение показываем во всех состояниях, включая загрузку и ошибку:
    // раздел в разработке одинаково верно и когда цифры не отрисовались
    const byChart = useMemo(() => {
        const map = {};
        (insights?.insights || []).forEach(i => {
            (map[i.chart] = map[i.chart] || []).push(i);
        });
        return map;
    }, [insights]);

    const targets = useMemo(() => {
        const map = {};
        (insights?.insights || []).forEach(i => {
            if (i.target_key) map[`${i.chart}:${i.target_key}`] = true;
        });
        return map;
    }, [insights]);

    if (loading && !hasLoadedOnce) {
        return (
            <div className={s.wrap}>
                <DevBanner/>
                <LoadingScreen/>
            </div>
        );
    }

    // Полноэкранная ошибка - только пока на экране ещё нечего показывать.
    // Ошибка на повторной загрузке (сменили фильтр, а сеть моргнула) не должна
    // прятать уже отрисованный дашборд - старые цифры лучше, чем ничего
    if (error && !hasLoadedOnce) {
        return (
            <div className={s.wrap}>
                <DevBanner/>
                <div className={s.error}>{error}</div>
                <button className={s.button} onClick={load}>Повторить</button>
            </div>
        );
    }

    const hasData = status?.has_data;

    return (
        <div className={s.wrap}>
            <DevBanner/>
            <div className={s.head}>
                <div>
                    <div className={s.title}>Аналитика найма</div>
                    <div className={s.subtitle}>
                        {status?.last_sync_at
                            ? `Данные на ${new Date(status.last_sync_at).toLocaleString('ru-RU')}`
                            : 'Данные ещё не собирались'}
                        {options.dataSince && ` · история с ${new Date(options.dataSince).toLocaleDateString('ru-RU')}`}
                    </div>
                </div>
                <div className={s.headActions}>
                    <button className={s.buttonGhost} onClick={() => setSettingsOpen(v => !v)}>
                        Настройки
                    </button>
                    <button className={s.buttonGhost} onClick={exportXlsx} disabled={exporting}>
                        {exporting ? 'Выгружаем...' : 'Экспорт в Excel'}
                    </button>
                    <button className={s.button} onClick={refresh} disabled={refreshing}>
                        {refreshing ? 'Обновляем...' : 'Обновить данные'}
                    </button>
                </div>
            </div>

            {settingsOpen && (
                <SettingsPanel
                    data={settings}
                    saving={settingsSaving}
                    onToggleDirection={toggleDirection}
                    onToggleChart={toggleChart}
                    onSetHiringType={setHiringType}
                    onSave={saveSettings}
                    onClose={() => setSettingsOpen(false)}
                />
            )}

            {notice && <div className={s.notice}>{notice}</div>}
            {/* Сюда попадает только ошибка ПОВТОРНОЙ загрузки - при первой
                отработал бы полноэкранный вариант выше, и до этой точки
                рендер бы не дошёл */}
            {error && (
                <div className={s.error}>
                    {error} <button className={s.buttonGhost} onClick={load}>Повторить</button>
                </div>
            )}

            {!hasData ? (
                <div className={s.empty}>
                    Данных пока нет. Нажмите «Обновить данные» - мы заберём историю вашей
                    воронки из Битрикс24. Первый сбор берёт год назад и может занять
                    несколько заходов.
                </div>
            ) : (
                // Затемняем при повторной загрузке вместо замены на "Загрузка...":
                // фильтр меняют, чтобы сравнить цифры, а не чтобы посмотреть
                // на пустой экран между одним набором цифр и другим
                <div className={loading ? s.contentRefreshing : ''}>
                    <div className={s.filters}>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>Сотрудник</label>
                            <Select
                                styles={selectStyles}
                                isClearable
                                placeholder="Все"
                                options={options.managers.map(m => ({
                                    value: m.id, label: m.name || `ID ${m.id}`,
                                }))}
                                value={(() => {
                                    const m = options.managers.find(x => x.id === filters.assignedById);
                                    return m ? { value: m.id, label: m.name || `ID ${m.id}` } : null;
                                })()}
                                onChange={o => setFilters(f => ({ ...f, assignedById: o?.value ?? null }))}
                            />
                        </div>
                        <div className={s.filterField}>
                            <label className={s.filterLabel}>Вакансия</label>
                            <Select
                                styles={selectStyles}
                                isClearable
                                placeholder="Все"
                                options={options.vacancies.map(v => ({ value: v.id, label: v.title || `#${v.id}` }))}
                                value={(() => {
                                    const v = options.vacancies.find(x => x.id === filters.vacancyId);
                                    return v ? { value: v.id, label: v.title || `#${v.id}` } : null;
                                })()}
                                onChange={o => setFilters(f => ({ ...f, vacancyId: o?.value ?? null }))}
                            />
                        </div>
                        <div className={`${s.filterField} ${s.filterFieldAuto}`} style={{ position: 'relative' }}>
                            <label className={s.filterLabel}>Период</label>
                            <button
                                type="button"
                                className={`${s.periodCustomBtn} ${filters.periodMode !== 'all' ? s.periodCustomBtnActive : ''}`}
                                onClick={() => setShowCalendar(v => !v)}
                            >
                                {periodTriggerLabel(filters)}
                                <span className={s.periodTriggerCaret}>▾</span>
                            </button>
                            {showCalendar && (
                                <>
                                    <div className={s.calendarBackdrop} onClick={() => setShowCalendar(false)}/>
                                    <PeriodMenu
                                        filters={filters}
                                        onSelectPreset={mode => setFilters(f => ({ ...f, periodMode: mode }))}
                                        onShift={dir => setFilters(f => ({
                                            ...f, periodAnchor: shiftPeriod(f.periodMode, f.periodAnchor, dir),
                                        }))}
                                        onApplyCustom={(range) => {
                                            setFilters(f => ({ ...f, periodMode: 'custom', customRange: range }));
                                            setShowCalendar(false);
                                        }}
                                        onClose={() => setShowCalendar(false)}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className={`${s.tiles} ${s.hiringStatsTiles}`}>
                        <StatTile
                            label="Срок закрытия вакансии"
                            value={days(data.hiringStats?.median_days_to_fill)}
                            hint="Медиана. Каждый нанятый - своё закрытие"
                        />
                        <StatTile
                            label="Закрыто вакансий"
                            value={data.hiringStats?.vacancies_closed ?? 0}
                            hint="За период и фильтры"
                        />
                        <StatTile
                            label="Кандидатов на HR"
                            value={data.hiringStats?.avg_candidates_per_manager ?? '-'}
                            hint="В среднем"
                        />
                    </div>

                    <Insights
                        data={insights}
                        loading={insightsLoading}
                        error={insightsError}
                        onAsk={askInsights}
                    />

                    <div className={`${s.card} ${isHidden('hiring_funnel') ? s.hiddenCard : ''}`}>
                        <CardTitle id="hiring_funnel" title="Воронка найма"
                                   collapsed={isCollapsed('hiring_funnel')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('hiring_funnel') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Кандидат на этапе «Собеседование» засчитан и на всех этапах
                            до него, даже если историю туда не завели отдельными событиями -
                            иначе воронка не убывала бы монотонно сверху вниз. Последний этап -
                            выход на работу. Число рядом с полосой слева - сколько дошло сюда
                            и какой процент из них пошёл дальше, к следующему этапу. Справа -
                            отказы после этого этапа, по причине. Клик по сегменту открывает
                            карточки
                        </div>
                        {data.hiringFunnel.length === 0 ? (
                            <div className={s.empty}>Данных о прохождении этапов пока нет.</div>
                        ) : byCategory(data.hiringFunnel).map(group => {
                            // StageRejectionRow не несёт category_id (это признак самого отказа,
                            // а не воронки) - сопоставляем по набору stage_id этой воронки.
                            // У смарт-процессов категория и так зашита в сам stage_id
                            // (DTxxx_yy:...), так что этого достаточно
                            const stageIds = new Set(group.rows.map(r => r.stage_id));
                            const rejectionRows = data.stageRejections.filter(r => stageIds.has(r.stage_id));
                            return (
                            <div key={group.id ?? 'none'}>
                                {byCategory(data.hiringFunnel).length > 1 && (
                                    <div className={s.subLabel}>
                                        {group.name || `Направление ${group.id}`}
                                    </div>
                                )}
                                <HiringFunnelChart
                                    pipelineRows={group.rows}
                                    rejectionRows={rejectionRows}
                                    onPipelineClick={(stage, seg) => openDrill(
                                        `Этап: ${stage.stageName || stage.stageId} · ${seg.label}`,
                                        {
                                            funnelStageId: stage.stageId,
                                            funnelEntityTypeId: stage.entityTypeId,
                                            funnelCategoryId: stage.categoryId,
                                            funnelSortOrder: stage.sortOrder,
                                            ...sourceOverrides(seg),
                                        },
                                    )}
                                    onRejectionClick={(stage, seg) => openDrill(
                                        `Отказ после этапа: ${stage.stageName || stage.stageId} · ${seg.label}`,
                                        {
                                            rejectedAfterStage: stage.stageId,
                                            rejectionReason: seg.reason || undefined,
                                        },
                                    )}
                                />
                            </div>
                            );
                        })}
                        <ChartInsights items={byChart.hiring_funnel}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('source') ? s.hiddenCard : ''}`}>
                        <CardTitle id="source" title="Откуда кандидаты"
                                   collapsed={isCollapsed('source')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('source') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Канал и то, как кандидат попал в работу: откликнулся сам на HH
                            или карточку завели вручную через расширение. Источник сейчас
                            один - HH, других каналов пока не подключено
                        </div>
                        {data.sourceBreakdown.length === 0 ? (
                            <div className={s.empty}>Кандидатов под выбранные фильтры нет.</div>
                        ) : (
                            <SourceBar rows={data.sourceBreakdown}/>
                        )}
                        <ChartInsights items={byChart.source}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('stage_sources') ? s.hiddenCard : ''}`}>
                        <CardTitle id="stage_sources" title="Кто сейчас на каком этапе"
                                   collapsed={isCollapsed('stage_sources')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('stage_sources') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Не кто побывал, а кто сейчас живой стоит на этапе, и из какого
                            источника. Наведите на сегмент для подписи, кликните - откроются
                            карточки именно этого среза
                        </div>
                        {data.stageSources.length === 0 ? (
                            <div className={s.empty}>Кандидатов под выбранные фильтры нет.</div>
                        ) : (
                            <StageSourceChart
                                rows={data.stageSources}
                                onSegmentClick={(stage, seg) => openDrill(
                                    `Этап: ${stage.stageName || stage.stageId} · ${seg.label}`,
                                    { stageId: stage.stageId, ...sourceOverrides(seg) },
                                )}
                            />
                        )}
                        <ChartInsights items={byChart.stage_sources}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('hr_load') ? s.hiddenCard : ''}`}>
                        <CardTitle id="hr_load" title="Загрузка эйчаров"
                                   collapsed={isCollapsed('hr_load')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('hr_load') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Та же воронка, что и выше, но раскрытая на всех сотрудников разом -
                            у каждого своя независимая воронка. Фильтр «Сотрудник» наверху экрана
                            сузит список, как и везде
                        </div>
                        {hrFunnelLoading ? (
                            <div className={s.empty}>Считаем воронку...</div>
                        ) : hrFunnelPipeline.length === 0 ? (
                            <div className={s.empty}>Кандидатов под выбранные фильтры нет.</div>
                        ) : byManager(hrFunnelPipeline).map((managerGroup, i) => {
                            const managerName = managerGroup.name || 'Без ответственного';
                            const managerRejections = hrFunnelRejections.filter(
                                r => (r.assigned_by_id ?? null) === (managerGroup.id ?? null)
                            );
                            const categoryGroups = byCategory(managerGroup.rows);
                            return (
                                <div key={managerGroup.id ?? 'none'} className={s.hrFunnelManagerBlock}>
                                    <div className={s.hrFunnelManagerHeading}>
                                        <span
                                            className={s.hrFunnelAvatar}
                                            style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
                                        >
                                            {managerName.trim().charAt(0).toUpperCase() || '?'}
                                        </span>
                                        <span className={s.hrFunnelManagerName}>{managerName}</span>
                                    </div>
                                    {categoryGroups.map(catGroup => {
                                        const stageIds = new Set(catGroup.rows.map(r => r.stage_id));
                                        const rejectionRows = managerRejections.filter(
                                            r => stageIds.has(r.stage_id)
                                        );
                                        return (
                                            <div key={catGroup.id ?? 'none'}>
                                                {categoryGroups.length > 1 && (
                                                    <div className={s.subLabel}>
                                                        {catGroup.name || `Направление ${catGroup.id}`}
                                                    </div>
                                                )}
                                                <HiringFunnelChart
                                                    pipelineRows={catGroup.rows}
                                                    rejectionRows={rejectionRows}
                                                    onPipelineClick={(stage, seg) => openDrill(
                                                        `${managerName} · Этап: ${stage.stageName || stage.stageId} · ${seg.label}`,
                                                        {
                                                            funnelStageId: stage.stageId,
                                                            funnelEntityTypeId: stage.entityTypeId,
                                                            funnelCategoryId: stage.categoryId,
                                                            funnelSortOrder: stage.sortOrder,
                                                            ...managerOverrides(managerGroup.id),
                                                            ...sourceOverrides(seg),
                                                        },
                                                    )}
                                                    onRejectionClick={(stage, seg) => openDrill(
                                                        `${managerName} · Отказ после этапа: ${stage.stageName || stage.stageId} · ${seg.label}`,
                                                        {
                                                            rejectedAfterStage: stage.stageId,
                                                            rejectionReason: seg.reason || undefined,
                                                            ...managerOverrides(managerGroup.id),
                                                        },
                                                    )}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                        </div>
                        </div>
                    </div>

                    {SHOW_LEGACY_CHARTS && (
                    <>
                    <div className={s.sectionLabel}>Динамика</div>

                    <div className={`${s.card} ${isHidden('timeline') ? s.hiddenCard : ''}`}>
                        <CardTitle id="timeline" title="Как меняется найм"
                                   collapsed={isCollapsed('timeline')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('timeline') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Наймы и приток кандидатов столбиками, срок найма линией.
                            Шаг {data.timeline.bucket === 'week' ? 'недельный' : 'месячный'} -
                            выбирается по длине периода
                        </div>
                        {data.timeline.points.length < 2 ? (
                            <div className={s.empty}>
                                Для динамики нужно хотя бы два периода с данными.
                                Пока их меньше - смотрите числа выше.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <ComposedChart
                                    data={data.timeline.points.map(p => ({
                                        name: p.label,
                                        fullLabel: p.label,
                                        hires: p.hires,
                                        newCandidates: p.new_candidates,
                                        median: p.median_days,
                                    }))}
                                    margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
                                >
                                    <CartesianGrid stroke={GRID} vertical={false}/>
                                    <XAxis dataKey="name" {...AXIS} tickLine={false} axisLine={false}/>
                                    {/* Две оси здесь оправданы: слева люди, справа дни.
                                        Это разные единицы, а не два способа показать одно */}
                                    <YAxis yAxisId="people" {...AXIS} tickLine={false} axisLine={false}/>
                                    <YAxis yAxisId="days" orientation="right" {...AXIS}
                                           tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Наймов', p.hires],
                                            ['Новых кандидатов', p.newCandidates],
                                            p.median != null && ['Обычно нанимали за', days(p.median)],
                                        ]}/>}
                                    />
                                    <Bar yAxisId="people" dataKey="newCandidates" fill="#bfdbfe"
                                         radius={[4, 4, 0, 0]} barSize={18}/>
                                    <Bar yAxisId="people" dataKey="hires" fill="#15803d"
                                         radius={[4, 4, 0, 0]} barSize={18}/>
                                    <Line yAxisId="days" type="monotone" dataKey="median"
                                          stroke="#b45309" strokeWidth={2} dot={{ r: 3 }}
                                          connectNulls/>
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                        <Legend items={[
                            { key: 'new', label: 'Новые кандидаты', color: '#bfdbfe' },
                            { key: 'hires', label: 'Наймы', color: '#15803d' },
                            { key: 'median', label: 'Срок найма, дни', color: '#b45309' },
                        ]}/>
                        <ChartInsights items={byChart.timeline}/>
                        </div>
                        </div>
                    </div>

                    <div className={s.sectionLabel}>Воронка</div>

                    <div className={`${s.card} ${isHidden('funnel') ? s.hiddenCard : ''}`}>
                        <CardTitle id="funnel" title="Прохождение этапов"
                                   collapsed={isCollapsed('funnel')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('funnel') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Сколько кандидатов побывало на каждом этапе, а не сидит там
                            сейчас. Каждое направление показано отдельно: наборы этапов
                            в них разные, и складывать их в один список нельзя. Доля -
                            от всех кандидатов своего направления, поэтому этапы не
                            обязаны убывать сверху вниз: в первый этап попадают не все
                        </div>
                        {data.funnel.length === 0 ? (
                            <div className={s.empty}>
                                Данных о прохождении этапов пока нет.
                            </div>
                        ) : byCategory(data.funnel).map(group => (
                            <div key={group.id ?? 'none'}>
                                {/* Заголовок направления рисуем, только если их
                                    несколько: на одной воронке он лишний шум */}
                                {byCategory(data.funnel).length > 1 && (
                                    <div className={s.subLabel}>
                                        {group.name || `Направление ${group.id}`}
                                    </div>
                                )}
                                <ResponsiveContainer width="100%" height={chartHeight(group.rows.length)}>
                                    <BarChart
                                        data={group.rows.map(f => ({
                                            name: shortLabel(f.stage_name || f.stage_id),
                                            fullLabel: f.stage_name || f.stage_id,
                                            stageId: f.stage_id,
                                            candidates: f.candidates,
                                            conversion: f.conversion,
                                            dropped: f.dropped,
                                            total: f.total_candidates,
                                        }))}
                                        layout="vertical"
                                        margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                    >
                                        <CartesianGrid stroke={GRID} horizontal={false}/>
                                        <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                                        <YAxis type="category" dataKey="name" width={150}
                                               {...AXIS} tickLine={false} axisLine={false}/>
                                        <Tooltip
                                            cursor={{ fill: '#f8fafc' }}
                                            content={<ChartTooltip rows={p => [
                                                ['Кандидатов', p.candidates],
                                                p.conversion != null && ['Доля от всех', `${Math.round(p.conversion * 100)}%`],
                                                p.dropped != null && ['Остановились здесь', p.dropped],
                                                p.total ? ['Всего в направлении', p.total] : null,
                                            ]}/>}
                                        />
                                        <Bar dataKey="candidates" radius={[0, 4, 4, 0]}
                                             barSize={16} cursor="pointer"
                                             onClick={(p) => openDrill(`Этап: ${p.fullLabel}`, { stageId: p.stageId })}>
                                            {group.rows.map(f => (
                                                <Cell key={f.stage_id}
                                                      fill={barFill(targets[`funnel:${f.stage_id}`], ACCENT, '#1e3a8a')}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ))}
                        <ChartInsights items={byChart.funnel}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('transitions') ? s.hiddenCard : ''}`}>
                        <CardTitle id="transitions" title="Куда идут кандидаты"
                                   collapsed={isCollapsed('transitions')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('transitions') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Переходы между конкретными этапами - в отличие от воронки видно,
                            откуда кандидаты пришли и куда ушли дальше. Янтарным - возврат
                            на более ранний этап. Клик по строке открывает карточки на том
                            этапе, куда вёл переход
                        </div>
                        {data.transitions.length === 0 ? (
                            <div className={s.empty}>Переходов между этапами пока нет.</div>
                        ) : byCategory(data.transitions).map(group => (
                            <div key={group.id ?? 'none'}>
                                {byCategory(data.transitions).length > 1 && (
                                    <div className={s.subLabel}>
                                        {group.name || `Направление ${group.id}`}
                                    </div>
                                )}
                                <TransitionsChart rows={group.rows} onDrill={openDrill}/>
                            </div>
                        ))}
                        </div>
                        </div>
                    </div>

                    <div className={s.sectionLabel}>Сроки подробно</div>

                    <div className={`${s.card} ${isHidden('time_to_hire') ? s.hiddenCard : ''}`}>
                        <CardTitle id="time_to_hire" title="Срок найма кандидата"
                                   collapsed={isCollapsed('time_to_hire')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('time_to_hire') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Сколько времени проходит от появления кандидата в базе до
                            того, как его наняли. Считается по каждому кандидату
                            отдельно. В профессиональной литературе это Time to Hire
                        </div>
                        <div className={s.tiles}>
                            <StatTile
                                label="Почти всегда быстрее"
                                value={days(data.timeToHire?.p90_days)}
                                hint="В этот срок укладываются девять кандидатов из десяти"
                            />
                            <StatTile
                                label="От и до"
                                value={`${days(data.timeToHire?.fastest_days)} - ${days(data.timeToHire?.slowest_days)}`}
                                hint="Самый быстрый и самый долгий наём за период"
                            />
                        </div>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('time_to_fill') ? s.hiddenCard : ''}`}>
                        <CardTitle id="time_to_fill" title="Срок закрытия вакансии"
                                   collapsed={isCollapsed('time_to_fill')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('time_to_fill') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Сколько вакансия остаётся открытой: от её создания до
                            найма первого человека. Если по вакансии берут нескольких,
                            срок считается по первому. В профессиональной литературе
                            это Time to Fill
                        </div>
                        <div className={s.tiles}>
                            <StatTile
                                label="Почти всегда быстрее"
                                value={days(data.timeToFill?.p90_days)}
                                hint="В этот срок укладываются девять вакансий из десяти"
                            />
                            <StatTile
                                label="Закрыто за период"
                                value={data.timeToFill?.filled ?? 0}
                                hint="Вакансий, по которым кто-то вышел"
                            />
                        </div>
                        {/* Пустой дашборд из-за незаполненного поля выглядит поломкой -
                            объясняем причину прямо здесь */}
                        {data.timeToFill?.candidates_without_vacancy > 0 && (
                            <div className={s.notice} style={{ marginTop: '12px', marginBottom: 0 }}>
                                У {data.timeToFill.candidates_without_vacancy} кандидатов не
                                заполнено поле вакансии - такие кандидаты не закрывают вакансию,
                                и в этой метрике они не учитываются.
                            </div>
                        )}
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('stage_durations') ? s.hiddenCard : ''}`}>
                        <CardTitle id="stage_durations" title="Сколько ждут на этапах"
                                   collapsed={isCollapsed('stage_durations')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('stage_durations') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Сколько кандидат обычно ждёт на каждом этапе. Те, кто
                            стоит на этапе прямо сейчас, в расчёт не входят - их срок
                            ещё не закончился, и они показаны отдельно
                        </div>
                        {candidateStages.length === 0 && vacancyStages.length === 0 ? (
                            <div className={s.empty}>
                                Пока нет ни одного завершённого перехода между стадиями.
                            </div>
                        ) : (
                            <>
                                {byCategory(candidateStages).map(group => (
                                    <div key={`c-${group.id ?? 'none'}`}>
                                        {byCategory(candidateStages).length > 1 && (
                                            <div className={s.subLabel}>
                                                {group.name || `Направление ${group.id}`}
                                            </div>
                                        )}
                                        <StageDurationChart rows={group.rows} targets={targets}
                                                            onDrill={openDrill}/>
                                    </div>
                                ))}
                                {vacancyStages.length > 0 && (
                                    <>
                                        <div className={s.subLabel}>Этапы самих вакансий</div>
                                        <div className={s.cardHint}>
                                            Это жизненный цикл вакансии, а не путь кандидата:
                                            сколько она висит на каждом своём этапе
                                        </div>
                                        <StageDurationChart rows={vacancyStages} targets={targets}
                                                            onDrill={openDrill} includeVacancies/>
                                    </>
                                )}
                            </>
                        )}
                        <ChartInsights items={byChart.stage_durations}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('difficulty') ? s.hiddenCard : ''}`}>
                        <CardTitle id="difficulty" title="Сложность найма по вакансиям"
                                   collapsed={isCollapsed('difficulty')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('difficulty') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            Насколько тяжело даётся вакансия: расход воронки, срок,
                            доля отказов, качество потока и возвраты кандидатов назад.
                            Это <b>ранг среди ваших же вакансий</b>, а не абсолютная
                            величина - с другим порталом такие числа сравнивать нельзя
                            {data.difficulty.length < 4 && data.difficulty.length > 0 &&
                                '. Вакансий пока мало, поэтому разброс крайний'}
                        </div>
                        {data.difficulty.length === 0 ? (
                            <div className={s.empty}>Вакансий под выбранные фильтры нет.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={chartHeight(data.difficulty.length)}>
                                <BarChart
                                    data={data.difficulty.map(v => ({
                                        name: shortLabel(v.title || `#${v.vacancy_id}`),
                                        fullLabel: `${v.title || `#${v.vacancy_id}`}${v.is_open ? ' (в работе)' : ''}`,
                                        vacancyId: v.vacancy_id,
                                        difficulty: v.difficulty,
                                        candidates: v.candidates,
                                        hires: v.hires,
                                        duration: v.duration_days,
                                        rejection: v.rejection_rate,
                                        score: v.median_score,
                                        backtracks: v.backtracks,
                                        isOpen: v.is_open,
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                >
                                    <CartesianGrid stroke={GRID} horizontal={false}/>
                                    <XAxis type="number" domain={[0, 100]} {...AXIS}
                                           tickLine={false} axisLine={false}/>
                                    <YAxis type="category" dataKey="name" width={150}
                                           {...AXIS} tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Насколько тяжело, из 100', p.difficulty],
                                            ['Кандидатов', p.candidates],
                                            ['Наймов', p.hires],
                                            p.duration != null && [p.isOpen ? 'Открыта' : 'Закрыта за', days(p.duration)],
                                            p.rejection != null && ['Доля отказов', `${Math.round(p.rejection * 100)}%`],
                                            p.score != null && ['Обычная оценка кандидата', p.score],
                                            p.backtracks > 0 && ['Возвратов назад', p.backtracks],
                                        ]}/>}
                                    />
                                    <Bar dataKey="difficulty" radius={[0, 4, 4, 0]} barSize={16}
                                         cursor="pointer"
                                         onClick={(p) => openDrill(`Вакансия: ${p.fullLabel}`, { vacancyId: p.vacancyId })}>
                                        {/* Незакрытые приглушены: они в работе, и их индекс
                                            ещё может измениться */}
                                        {data.difficulty.map(v => (
                                            <Cell key={v.vacancy_id}
                                                  fill={barFill(
                                                      targets[`difficulty:${v.vacancy_id}`],
                                                      v.is_open ? '#93c5fd' : ACCENT,
                                                      '#1e3a8a',
                                                  )}/>
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        <ChartInsights items={byChart.difficulty}/>
                        </div>
                        </div>
                    </div>

                    <div className={`${s.card} ${isHidden('manager_load') ? s.hiddenCard : ''}`}>
                        <CardTitle id="manager_load" title="Загрузка сотрудников"
                                   collapsed={isCollapsed('manager_load')} onToggle={toggleCollapse}/>
                        <div className={`${s.cardBodyWrap} ${isCollapsed('manager_load') ? s.cardBodyWrapCollapsed : ''}`}>
                        <div className={s.cardBody}>
                        <div className={s.cardHint}>
                            {/* Свежесть подписываем честно: без периода это состояние
                                на момент последнего обновления, а не «прямо сейчас» */}
                            {data.managerLoad?.source === 'snapshot'
                                ? `Как было на ${new Date(data.managerLoad.as_of).toLocaleDateString('ru-RU')} - последний день выбранного периода, по которому есть данные`
                                : data.managerLoad?.as_of
                                    ? `Сколько карточек у кого по данным на ${new Date(data.managerLoad.as_of).toLocaleString('ru-RU')}`
                                    : 'Сколько карточек у кого'}
                        </div>
                        {/* Период выбран, но слепков за него нет - показываем текущее
                            и говорим об этом, иначе цифры выдадут себя за прошлые */}
                        {data.managerLoad?.source === 'current' && filters.periodMode !== 'all' && (
                            <div className={s.notice} style={{ marginBottom: '12px' }}>
                                {filters.vacancyId
                                    ? 'Загрузка за период не разбивается по вакансиям - показано текущее состояние по выбранной вакансии.'
                                    : 'За выбранный период данных о загрузке ещё нет - они копятся с момента подключения аналитики. Показано текущее состояние.'}
                            </div>
                        )}
                        <Legend items={SEMANTIC_ORDER.map(k => ({ key: k, ...SEMANTICS[k] }))}/>
                        {managerRows.length === 0 ? (
                            <div className={s.empty}>Нет карточек под выбранные фильтры.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={chartHeight(managerRows.length)}>
                                <BarChart
                                    data={managerRows.map(row => ({
                                        name: shortLabel(row.name || (row.id ? `ID ${row.id}` : 'Без ответственного')),
                                        fullLabel: row.name || (row.id ? `ID ${row.id}` : 'Без ответственного'),
                                        managerId: row.id,
                                        total: row.total,
                                        ...Object.fromEntries(
                                            SEMANTIC_ORDER.map(k => [k, row.bySemantic[k] || 0])
                                        ),
                                    }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                                >
                                    <CartesianGrid stroke={GRID} horizontal={false}/>
                                    <XAxis type="number" {...AXIS} tickLine={false} axisLine={false}/>
                                    <YAxis type="category" dataKey="name" width={150}
                                           {...AXIS} tickLine={false} axisLine={false}/>
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        content={<ChartTooltip rows={p => [
                                            ['Всего карточек', p.total],
                                            ...SEMANTIC_ORDER
                                                .filter(k => p[k])
                                                .map(k => [SEMANTICS[k].label, p[k]]),
                                        ]}/>}
                                    />
                                    {SEMANTIC_ORDER.map((k, i) => (
                                        <Bar key={k} dataKey={k} stackId="load"
                                             fill={SEMANTICS[k].color} barSize={16} cursor="pointer"
                                             radius={i === SEMANTIC_ORDER.length - 1 ? [0, 4, 4, 0] : 0}
                                             onClick={(p) => openDrill(`Сотрудник: ${p.fullLabel}`, { assignedById: p.managerId })}/>
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        <ChartInsights items={byChart.manager_load}/>
                        </div>
                        </div>
                    </div>
                    </>
                    )}
                </div>
            )}

            {drill && (
                <DrillPanel
                    title={drill.title}
                    cards={drillCards}
                    loading={drillLoading}
                    host={portalHost}
                    onClose={() => setDrill(null)}
                />
            )}
        </div>
    );
}
